import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { XAlertService } from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestService } from '@core/services/request/request.service';
import { TuiAccordion } from '@taiga-ui/kit';
import {
  TuiError,
  TuiButton,
  TuiLabel,
  TuiCheckbox,
  TUI_VALIDATION_ERRORS,
  TuiInput,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  RequestFileCategory,
  RequestFileCategoryGroup,
  RequestFormFilesSendByEmailTranslations,
  RequestFormFilesSendByEmailTranslationsPayload,
} from '@core/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileGroupsWithFilesToSend } from '@features/requests/request-form/components/request-form-files/types';
import { catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';

enum FormFields {
  EmailToSendFiles = 'emailToSendFiles',
}

@Component({
  selector: 'x-request-files-send-by-email',
  imports: [
    TuiTextfield,
    CommonModule,
    TuiInput,
    ReactiveFormsModule,
    TuiError,
    TuiLabel,
    TuiAccordion,
    TuiCheckbox,
    TuiButton,
    FormsModule,
    TranslatePipe,
  ],
  templateUrl: './request-files-send-by-email.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: {
        required: 'Email обязателен!',
        email: 'Введите корректный email!',
      },
    },
  ],
})
export class RequestFilesSendByEmailComponent implements OnInit {
  public readonly permissionService = inject(OutkeepPermissionService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly requestService = inject(RequestService);
  private readonly $request = this.requestService.$request;
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private translates: RequestFormFilesSendByEmailTranslationsPayload = {
    [RequestFormFilesSendByEmailTranslations.NoFilesSelected]:
      'Выберите файлы для отправки!',
    [RequestFormFilesSendByEmailTranslations.SuccessSendingFiles]:
      'Файлы отправлены на почту',
    [RequestFormFilesSendByEmailTranslations.ErrorSendingFiles]:
      'Невозможно отправить файлы заявки!',
  };
  private readonly destroyRef = inject(DestroyRef);
  readonly emailToSendFiles = new FormControl('', [
    Validators.required,
    Validators.email,
  ]);
  FormFields = FormFields;

  formGroup = new FormGroup({
    [FormFields.EmailToSendFiles]: this.emailToSendFiles,
  });
  $fileCatGroups = input<RequestFileCategoryGroup[]>([], {
    alias: 'fileCatGroups',
  });
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });
  public $sending = signal(false);
  /**
   * Выбор файлов для отправки на бекенд (предварительный)
   */
  public selectedFilesToDownload: Record<
    RequestFileCategoryGroup['id'],
    Record<RequestFileCategory['id'], boolean>
  > = {};
  /**
   * Для состояния чекбокса indeterminate - когда выбрана часть файлов, но не все
   */
  public someFilesInGroupSelected: Record<
    RequestFileCategoryGroup['id'],
    boolean
  > = {};
  /**
   * Для чекбокса "выбрать все" в группе файлов
   */
  public groupFilesAllSelected: Record<
    RequestFileCategoryGroup['id'],
    boolean | undefined
  > = {};

  constructor() {
    effect(() => {
      if (this.$fileCatGroups().length) {
        this.$fileCatGroups().forEach(group => {
          this.toggleAllCatFiles(group.id, false);
          this.groupFilesAllSelected[group.id] = false;
        });
      }
    });
  }

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormFilesSendByEmailTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  onSelectAllGroupFiles(groupId: RequestFileCategoryGroup['id']): void {
    if (this.$sending()) return;

    const selectAll = this.groupFilesAllSelected[groupId] as boolean;

    this.toggleAllCatFiles(groupId, selectAll);
    this.someFilesInGroupSelected[groupId] = false;
  }

  toggleAllCatFiles(groupId: RequestFileCategoryGroup['id'], value: boolean) {
    const group = this.$fileCatGroups().find(group => group.id === groupId);

    if (group) {
      this.selectedFilesToDownload[groupId] = group.fileCats.reduce(
        (acc, val) => {
          acc[val.id] = value;

          return acc;
        },
        {} as Record<RequestFileCategory['id'], boolean>,
      );
    }
  }

  checkAllFilesSelected(groupId: RequestFileCategoryGroup['id']) {
    const allSelected = Object.values(
      this.selectedFilesToDownload[groupId],
    ).every(Boolean);
    const someSelected = Object.values(
      this.selectedFilesToDownload[groupId],
    ).some(Boolean);

    if (allSelected) {
      this.someFilesInGroupSelected[groupId] = false;
      this.groupFilesAllSelected[groupId] = true;
    } else if (someSelected) {
      this.someFilesInGroupSelected[groupId] = true;
      this.groupFilesAllSelected[groupId] = undefined;
    } else {
      this.someFilesInGroupSelected[groupId] = false;
      this.groupFilesAllSelected[groupId] = false;
    }
  }

  sendFilesByEmail() {
    // check this.selectedFilesToDownload for empty

    if (this.$sending() || this.emailToSendFiles.invalid) return;

    if (
      !Object.values(this.selectedFilesToDownload).some(group =>
        Object.values(group).some(isSelected => isSelected),
      )
    ) {
      this.xAlertService.error(
        this.translates[
          RequestFormFilesSendByEmailTranslations.NoFilesSelected
        ],
      );

      return;
    }
    this.$sending.set(true);
    this.emailToSendFiles.disable();

    const fileGroupsWithFilesToSend: FileGroupsWithFilesToSend = Object.entries(
      this.selectedFilesToDownload,
    ).reduce(
      (
        acc: Record<string, number[]>,
        [key, value]: [string, Record<string, boolean>],
      ) => {
        const fileIds = Object.entries(value).reduce<number[]>(
          (fileIds, [fileId, isSelected]) => {
            if (isSelected) {
              fileIds.push(Number(fileId));
            }

            return fileIds;
          },
          [],
        );

        if (fileIds.length > 0) {
          acc[key] = fileIds;
        }

        return acc;
      },
      {},
    );

    this.requestFilesService
      .sendFilesByEmail$(
        this.$request().id,
        this.emailToSendFiles.value!,
        fileGroupsWithFilesToSend,
      )
      .pipe(
        catchError(err => {
          console.error(err);
          this.xAlertService.error(
            this.translates[
              RequestFormFilesSendByEmailTranslations.ErrorSendingFiles
            ],
            err.data ? err.data : `Статус: ${err.status}`,
          );
          this.$sending.set(false);
          this.emailToSendFiles.enable();

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.xAlertService.success(
          this.translates[
            RequestFormFilesSendByEmailTranslations.SuccessSendingFiles
          ] + this.emailToSendFiles.value,
        );
        this.$sending.set(false);
        this.emailToSendFiles.enable();
      });
  }
}
